import type {
  InventoryGroupCode,
  InventoryItem,
  InventoryItemType,
  InventoryStatus,
  ProductGroupCode,
} from '@/api/inventory';
import { createCentralRequest } from '@/api/inventory';
import { Button, Card, CardContent, Input } from '@/components/ui';
import {
  useInventory,
  useInventoryGroups,
  useLowStockAlerts,
  usePhantomCount,
  useWarehouses,
} from '@/hooks/use-inventory';
import { useServerPagination } from '@/hooks/use-server-pagination';
import { formatCurrency } from '@/lib/formatters';
import { useTranslation } from '@kgc/i18n';
import { DataTable } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PRODUCT_GROUP_CODES } from './product-groups';

const TYPE_LABELS: Record<InventoryItemType, string> = {
  PRODUCT: 'inventory.typeLabels.product',
  RENTAL_EQUIPMENT: 'inventory.typeLabels.rentalEquipment',
  PART: 'inventory.typeLabels.part',
  CONSUMABLE: 'inventory.typeLabels.consumable',
};

export function InventoryListPage() {
  const { t } = useTranslation();

  const STATUS_LABELS: Record<InventoryStatus, { label: string; color: string }> = {
    AVAILABLE: {
      label: t('inventory.available'),
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    },
    RESERVED: {
      label: t('status.reserved'),
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    IN_TRANSIT: {
      label: t('inventory.statusLabels.inTransit'),
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    },
    IN_SERVICE: {
      label: t('status.inService'),
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    },
    SOLD: {
      label: t('inventory.statusLabels.sold'),
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    },
    RENTED: {
      label: t('status.rentedOut'),
      color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
    },
    DAMAGED: {
      label: t('inventory.statusLabels.damaged'),
      color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    },
    LOST: {
      label: t('inventory.statusLabels.lost'),
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    },
    SCRAPPED: {
      label: t('inventory.statusLabels.scrapped'),
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    },
  };
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<InventoryItemType | ''>('');
  const [statusFilter, setStatusFilter] = useState<InventoryStatus | ''>('');
  const [warehouseFilter, setWarehouseFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<InventoryGroupCode | ''>('');
  const [productGroupFilter, setProductGroupFilter] = useState<ProductGroupCode | ''>('');

  const [centralRequestLoading, setCentralRequestLoading] = useState(false);
  const [centralRequestMsg, setCentralRequestMsg] = useState<string | null>(null);

  // Server-mode pagination (BE-S2 = A): a lista eddig page/pageSize nélkül hívott
  // → CSAK a BE-default első ~20 készlet-tételt mutatta (a 21+ némán láthatatlan volt).
  const serverPagination = useServerPagination(20);

  // API hooks
  const { items, total, isLoading, error, refetch } = useInventory({
    search: searchTerm || undefined,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    warehouseId: warehouseFilter || undefined,
    inventoryGroup: groupFilter || undefined,
    groupCode: productGroupFilter || undefined,
    ...serverPagination.query,
  });

  // Szűrő-váltáskor ugorj az 1. oldalra.
  useEffect(() => {
    serverPagination.reset();
  }, [
    searchTerm,
    typeFilter,
    statusFilter,
    warehouseFilter,
    groupFilter,
    productGroupFilter,
    serverPagination.reset,
  ]);
  const { alerts } = useLowStockAlerts();
  const { warehouses } = useWarehouses();
  const { groups: inventoryGroups } = useInventoryGroups();
  const { count: phantomCount } = usePhantomCount();

  const formatPrice = formatCurrency;

  // Oszlop-definíciók a megosztott DataTable-höz (a cella-renderek megőrzik az eredeti markup-ot).
  const columns: ColumnDef<InventoryItem>[] = [
    {
      id: 'product',
      header: t('inventory.typeLabels.product'),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{item.productName || 'N/A'}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {item.productSku || item.serialNumber || '-'}
            </p>
          </div>
        );
      },
    },
    {
      id: 'type',
      header: t('common.type'),
      cell: ({ row }) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t(TYPE_LABELS[row.original.type] ?? '') || row.original.type}
        </span>
      ),
    },
    {
      id: 'quantity',
      header: () => <div className="text-right">{t('common.quantity')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isLow = !!(item.minStockLevel && item.quantity <= item.minStockLevel);
        return (
          <div className="text-right">
            <span
              className={`font-medium ${isLow ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'}`}
            >
              {item.quantity}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400"> {item.unit}</span>
          </div>
        );
      },
    },
    {
      id: 'reserved',
      header: () => <div className="text-right">{t('inventory.reserved')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-right text-sm">
            {(item.reservedQuantity ?? 0) > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="font-medium text-orange-600 dark:text-orange-400">
                  {item.reservedQuantity}
                </span>
                <span className="rounded-full border border-orange-500 px-2 py-0.5 text-xs font-medium text-orange-600 dark:text-orange-400">
                  {t('inventory.reservedForWorksheet')}
                </span>
              </span>
            ) : (
              <span className="text-gray-600 dark:text-gray-400">-</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'available',
      header: () => <div className="text-right">{t('inventory.available')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-right">
            <span className="font-medium text-green-600 dark:text-green-400">
              {item.quantity - (item.reservedQuantity ?? 0)}
            </span>
          </div>
        );
      },
    },
    {
      id: 'purchasePrice',
      header: () => <div className="text-right">{t('inventory.purchasePrice')}</div>,
      cell: ({ row }) => (
        <div className="text-right font-medium text-gray-900 dark:text-white">
          {row.original.purchasePrice ? formatPrice(row.original.purchasePrice) : '-'}
        </div>
      ),
    },
    {
      id: 'warehouse',
      header: t('inventory.warehouse'),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-sm">
            <p className="text-gray-900 dark:text-white">{item.warehouseName || '-'}</p>
            {item.locationCode && (
              <p className="text-gray-500 dark:text-gray-400">{item.locationCode}</p>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: t('table.status'),
      cell: ({ row }) => (
        <span
          className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_LABELS[row.original.status]?.color || 'bg-muted text-foreground'}`}
        >
          {STATUS_LABELS[row.original.status]?.label || row.original.status}
        </span>
      ),
    },
  ];

  // Statistics computed from API data
  const stats = useMemo(
    () => ({
      totalItems: total,
      totalValue: items.reduce((sum, item) => sum + item.quantity * (item.purchasePrice || 0), 0),
      lowStock: alerts.length,
      available: items.filter(item => item.status === 'AVAILABLE').length,
      reserved: items.filter(item => item.status === 'RESERVED').length,
    }),
    [items, total, alerts]
  );

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('inventory.management')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('inventory.warehouseAndMovements')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/inventory/analytics')}>
              {t('inventory.analytics.title')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/inventory/movements')}>
              {t('inventory.movements')}
            </Button>
            <Button
              onClick={() => navigate('/goods-receipt/new')}
              className="bg-primary hover:bg-primary/90"
            >
              {t('inventory.newReceipt')}
            </Button>
          </div>
        </div>
        {/* INV-FG-7 Action bar */}
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 pb-3 sm:px-6 lg:px-8">
          {phantomCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
              {t('inventory.phantom.badge', { count: phantomCount })}
            </span>
          )}
          {alerts.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={centralRequestLoading}
              onClick={async () => {
                setCentralRequestLoading(true);
                setCentralRequestMsg(null);
                try {
                  const productIds = alerts.map(a => a.productId);
                  const result = await createCentralRequest(productIds);
                  setCentralRequestMsg(
                    t('inventory.centralRequest.success', { count: result.data.count })
                  );
                } catch {
                  setCentralRequestMsg(t('common.error'));
                } finally {
                  setCentralRequestLoading(false);
                }
              }}
            >
              {centralRequestLoading
                ? t('inventory.centralRequest.submitting')
                : t('inventory.centralRequest.title')}
            </Button>
          )}
          {centralRequestMsg && (
            <span className="text-sm text-green-600 dark:text-green-400">{centralRequestMsg}</span>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('inventory.allItems')}
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalItems}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('inventory.stockValue')}
              </p>
              <p className="mt-1 text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatPrice(stats.totalValue)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('inventory.available')}
              </p>
              <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.available}
              </p>
            </CardContent>
          </Card>
          <Card
            className={
              stats.lowStock > 0
                ? 'border-blue-300 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/30'
                : ''
            }
          >
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-muted-foreground">{t('inventory.lowStock')}</p>
              <p
                className={`mt-1 text-2xl font-bold ${stats.lowStock > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'}`}
              >
                {t('inventory.lowStockCountBadge', { count: stats.lowStock })}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4">
          <Input
            type="text"
            placeholder={t('inventory.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value as InventoryItemType | '')}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
          >
            <option value="">{t('page.allTypes')}</option>
            {Object.entries(TYPE_LABELS).map(([value, labelKey]) => (
              <option key={value} value={value}>
                {t(labelKey)}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as InventoryStatus | '')}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
          >
            <option value="">{t('page.allStatuses')}</option>
            {Object.entries(STATUS_LABELS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={warehouseFilter}
            onChange={e => setWarehouseFilter(e.target.value)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
          >
            <option value="">{t('inventory.allWarehouses')}</option>
            {warehouses.map(wh => (
              <option key={wh.id} value={wh.id}>
                {wh.name}
              </option>
            ))}
          </select>
          {inventoryGroups.length > 0 && (
            <select
              value={groupFilter}
              onChange={e => setGroupFilter(e.target.value as InventoryGroupCode | '')}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
            >
              <option value="">{t('inventory.allGroups')}</option>
              {inventoryGroups.map(g => (
                <option key={g.code} value={g.code}>
                  {t(`inventory.inventoryGroups.${g.code}`) !==
                  `inventory.inventoryGroups.${g.code}`
                    ? t(`inventory.inventoryGroups.${g.code}`)
                    : g.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={productGroupFilter}
            onChange={e => setProductGroupFilter(e.target.value as ProductGroupCode | '')}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
          >
            <option value="">{t('inventory.allProductGroups')}</option>
            {PRODUCT_GROUP_CODES.map(pg => (
              <option key={pg.code} value={pg.code}>
                {t(pg.labelKey) !== pg.labelKey ? t(pg.labelKey) : pg.code}
              </option>
            ))}
          </select>
        </div>

        {/* Loading state */}
        {isLoading && (
          <Card>
            <CardContent className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">{t('inventory.loadingStock')}</p>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="py-8 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">
                {t('page.errorPrefix', { message: error })}
              </p>
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
            </CardContent>
          </Card>
        )}

        {/* Inventory table */}
        {!isLoading && !error && (
          <Card>
            <CardContent className="p-0">
              <DataTable
                columns={columns}
                data={items}
                emptyMessage={t('inventory.noResults')}
                getRowId={item => item.id}
                rowClassName={item =>
                  item.minStockLevel && item.quantity <= item.minStockLevel
                    ? 'bg-blue-50/50 hover:bg-blue-50 dark:bg-blue-900/10 dark:hover:bg-blue-900/20'
                    : ''
                }
                {...(typeof total === 'number' ? serverPagination.tableProps(total) : {})}
                rowActions={item => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/inventory/${item.id}`)}
                  >
                    {t('common.details')}
                  </Button>
                )}
              />
            </CardContent>
          </Card>
        )}

        {/* Quick actions legend */}
        <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-green-500"></span>
            {t('inventory.availableCount', { count: stats.available })}
          </span>
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-yellow-500"></span>
            {t('inventory.reservedCount', { count: stats.reserved })}
          </span>
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-blue-500"></span>
            {t('inventory.lowStockCountBadge', { count: stats.lowStock })}
          </span>
        </div>
      </main>
    </div>
  );
}
